/**
 * Saved data on the device (GDD §4.7, §8, §10.4; D20): the high score and the settings. One versioned record in
 * localStorage that later versions migrate rather than drop, so it survives updates. Storage can be missing, full
 * or blocked (private windows), so every access is guarded and the game plays on without it.
 *
 * Version 1 (M3) held the high score; version 2 (M5) added the settings, with defaults for anything missing.
 * Version 3 turns auto-fire off by default (Mehdi, 2026-10-10), so saves from version 2 drop their old default.
 */
import { config } from '../core/config.ts';

const KEY = 'headwater.save';
const VERSION = 3;

/** The settings screen (GDD §8, §9). Volumes are 0..1; M6's mixer reads them. */
export interface Settings {
  /** Touch controls: fire while an enemy or intact bridge is in the line of fire (Q3). Off by default. */
  autoFire: boolean;
  /** Mirrors the steering pad and the fire button. */
  leftHanded: boolean;
  /** Steering sensitivity, 0.5×–1.5× (GDD §4.1). */
  sensitivity: number;
  haptics: boolean;
  music: number;
  effects: number;
  mute: boolean;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  autoFire: false,
  leftHanded: false,
  sensitivity: 1,
  haptics: true,
  music: 0.8,
  effects: 0.9,
  mute: false,
});

export interface SaveData {
  version: number;
  hi: number;
  settings: Settings;
}

/** The storage the game uses (window.localStorage in the browser; a stand-in in tests). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const bool = (v: unknown, d: boolean): boolean => (typeof v === 'boolean' ? v : d);
const num = (v: unknown, lo: number, hi: number, d: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d;

/** Settings from anything: each field checked and clamped, anything missing or wrong takes its default. */
export function readSettings(raw: unknown): Settings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_SETTINGS;
  const sens = config.flight.sensitivity;
  return {
    autoFire: bool(r.autoFire, d.autoFire),
    leftHanded: bool(r.leftHanded, d.leftHanded),
    sensitivity: num(r.sensitivity, sens.min, sens.max, d.sensitivity),
    haptics: bool(r.haptics, d.haptics),
    music: num(r.music, 0, 1, d.music),
    effects: num(r.effects, 0, 1, d.effects),
    mute: bool(r.mute, d.mute),
  };
}

/** Reads the save, migrating older versions; anything unreadable gives a fresh save (high score 0, D20). */
export function loadSave(store: KeyValueStore | null = defaultStore()): SaveData {
  const fresh: SaveData = { version: VERSION, hi: 0, settings: { ...DEFAULT_SETTINGS } };
  if (!store) return fresh;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return fresh;
    const d = JSON.parse(raw) as { version?: unknown; hi?: unknown; settings?: unknown };
    const hi = typeof d.hi === 'number' && Number.isFinite(d.hi) && d.hi > 0 ? Math.floor(d.hi) : 0;
    // Version 1 had no settings: readSettings fills the defaults.
    const settings = readSettings(d.settings);
    // Versions before 3 saved auto-fire on as the old default; it starts off now.
    if (!(typeof d.version === 'number' && d.version >= 3)) settings.autoFire = false;
    return { version: VERSION, hi, settings };
  } catch {
    return fresh;
  }
}

function write(store: KeyValueStore, data: SaveData): boolean {
  try {
    store.setItem(KEY, JSON.stringify({ version: VERSION, hi: data.hi, settings: data.settings }));
    return true;
  } catch {
    return false;
  }
}

/** Saves a new high score if it beats the stored one. Returns whether it was written. */
export function saveHigh(hi: number, store: KeyValueStore | null = defaultStore()): boolean {
  if (!store) return false;
  const cur = loadSave(store);
  if (!(hi > cur.hi)) return false;
  return write(store, { ...cur, hi: Math.floor(hi) });
}

/** Saves the settings, keeping the high score. Returns whether they were written. */
export function saveSettings(settings: Settings, store: KeyValueStore | null = defaultStore()): boolean {
  if (!store) return false;
  const cur = loadSave(store);
  return write(store, { ...cur, settings: readSettings(settings) });
}
